// src/components/CompetitionCurrentPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/CompetitionCurrentPage.css";
import { getCompetitionTypes, getTop10Entries } from "../api/competition";

const formatMoney = (val) => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "-";
  return `$${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fa-IR");
};

const rankMedal = (rank) => {
  if (rank === 1) return "/images/competitions/medal-gold.svg";
  if (rank === 2) return "/images/competitions/medal-silver.svg";
  if (rank === 3) return "/images/competitions/medal-bronze.svg";
  return null;
};

const CompetitionCurrentPage = () => {
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (showLoading = false) => {
      if (showLoading) setLoading(true);
      try {
        const res = await getCompetitionTypes();
        if (cancelled) return;
        const comps = Array.isArray(res?.competitions) ? res.competitions : [];
        const active = comps.find((c) => c.is_active) || comps[0] || null;
        setCompetition(active);
        if (active?.id) {
          const top = await getTop10Entries(active.id);
          if (cancelled) return;
          setEntries(Array.isArray(top) ? top : []);
        } else {
          setEntries([]);
        }
      } catch {
        setCompetition(null);
        setEntries([]);
      } finally {
        if (!cancelled && showLoading) setLoading(false);
      }
    };
    load(true);
    const refreshTimer = window.setInterval(() => load(false), 15000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, []);

  const podium = useMemo(() => {
    const winners = entries.slice(0, 3);
    return [
      { id: 1, title: "نفر اول", prize: formatMoney(winners[0]?.profit), medal: "/images/competitions/medal-gold.svg" },
      { id: 2, title: "نفر دوم", prize: formatMoney(winners[1]?.profit), medal: "/images/competitions/medal-silver.svg" },
      { id: 3, title: "نفر سوم", prize: formatMoney(winners[2]?.profit), medal: "/images/competitions/medal-bronze.svg" },
    ];
  }, [entries]);

  const tableRows = useMemo(
    () =>
      entries.map((entry, idx) => ({
        rank: idx + 1,
        name: entry.full_name || entry.user_email || "-",
        prize: formatMoney(entry.profit),
        date: formatDate(entry.join_time),
        status: competition?.is_active ? "در حال برگزاری" : "پایان یافته",
      })),
    [entries, competition]
  );

  const rulesItems = useMemo(() => {
    if (!competition) {
      return [
        "مسابقه فعال",
        "تاریخ شروع: -",
        "تاریخ پایان: -",
        "هزینه ورود: -",
      ];
    }
    const entryFee = Number(competition.entry_fee || 0);
    const entryFeeLabel = entryFee > 0 ? formatMoney(entryFee) : "رایگان";
    return [
      competition.name || "مسابقه فعال",
      `تاریخ شروع: ${formatDate(competition.start_time)}`,
      `تاریخ پایان: ${formatDate(competition.end_time)}`,
      `هزینه ورود: ${entryFeeLabel}`,
    ];
  }, [competition]);

  const tabLabel = competition?.type === "weekly" ? "مسابقه هفتگی" : "مسابقه ماهانه";

  return (
    <MenuLayout>
      <div className="comp-page">
        <header className="comp-header">
          <h1 className="comp-title">مسابقات</h1>
          <p className="comp-subtitle">
            رقابت کن، رتبه بگیر و جوایز این هفته را از دست نده.
          </p>
        </header>

        <div className="comp-hero">
          <div className="comp-join">
            <button className="comp-join-btn" onClick={() => navigate("/competitions")}>
              شرکت در مسابقه آینده
            </button>
            <div className="comp-podium">
              {podium.map((p) => (
                <div className="comp-podium-card" key={p.id}>
                  <img src={p.medal} alt={p.title} className="comp-medal" />
                  <div className="comp-podium-title">{p.title}</div>
                  <div className="comp-podium-prize">{p.prize}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="comp-rules">
            <div className="comp-tabs">
              <button className="comp-tab comp-tab-active">{tabLabel}</button>
            </div>
            <div className="comp-rules-box">
              <h3 className="comp-rules-title">قوانین مسابقه</h3>
              {rulesItems.map((item, idx) => (
                <p className="comp-rules-item" key={idx}>
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>

        <section className="comp-current">
          <div className="comp-current-title">مسابقات جاری</div>
          <div className="comp-current-table">
            <div className="comp-current-head">
              <span>رتبه</span>
              <span>نام شرکت کننده</span>
              <span>سود تجمیعی</span>
              <span>تاریخ</span>
              <span>وضعیت</span>
            </div>
            <div className="comp-current-body">
              {loading ? (
                <div className="comp-current-row">در حال دریافت...</div>
              ) : tableRows.length === 0 ? (
                <div className="comp-current-row">اطلاعاتی برای نمایش وجود ندارد.</div>
              ) : (
                tableRows.map((row) => (
                  <div className="comp-current-row" key={row.rank}>
                    <span className="comp-rank">
                      {rankMedal(row.rank) ? <img src={rankMedal(row.rank)} alt={`رتبه ${row.rank}`} /> : row.rank}
                    </span>
                    <span className="comp-user">{row.name}</span>
                    <span className="comp-prize">{row.prize}</span>
                    <span>{row.date}</span>
                    <span className="comp-status">{row.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    </MenuLayout>
  );
};

export default CompetitionCurrentPage;
